import { lifecycleUnits } from './lifecycle.units';
import { contracts } from './contracts';
import type { EntityDefinition, FieldSpec } from './types';

const presentation: Record<string, [string, string, string]> = {
  'transport-modes': ['Transport modes', 'Logistics', 'Maintain active transport modes.'],
  'plant-storage-locations': [
    'Plant storage locations',
    'Locations & facilities',
    'Maintain dated storage codes, including leading zeros.',
  ],
  'document-origin-countries': [
    'Document origin countries',
    'Documents & compliance',
    'Map countries to document origins.',
  ],
  'product-tariff-classifications': [
    'Tariff classifications',
    'Products',
    'Maintain dated tariff codes; only future versions can be corrected.',
  ],
  'product-information-records': [
    'Product information',
    'Products',
    'Keep Japan-issued source snapshots separate from editable USA working records.',
  ],
  'product-lifecycle-parameters': [
    'Lifecycle parameters',
    'Products',
    'Maintain shelf life, ship-by and deliver-by durations for each product.',
  ],
  'inventory-planning-settings': [
    'Inventory planning',
    'Products',
    'Maintain advisory inventory floors, ceilings and forecast thresholds.',
  ],
  'storage-room-assignments': [
    'Storage assignments',
    'Products',
    'Track which factory storage room holds a product over time.',
  ],
  uoms: [
    'Units of measure',
    'Products',
    'Maintain the shared units used by products and packaging conversions.',
  ],
  locations: [
    'Locations',
    'Locations & facilities',
    'Maintain plants, warehouses, customer sites, hubs and ports of entry.',
  ],
  factories: [
    'Factories',
    'Locations & facilities',
    'Connect each manufacturing factory to its physical location.',
  ],
  'storage-rooms': [
    'Storage rooms',
    'Locations & facilities',
    'Organize factory rooms by freeze, cool or room-temperature storage.',
  ],
  warehouses: [
    'Warehouses',
    'Locations & facilities',
    'Maintain warehouse identities and their locations.',
  ],
  'warehouse-storage-bins': [
    'Warehouse bins',
    'Locations & facilities',
    'Organize storage bins and zones within each warehouse.',
  ],
  customers: [
    'Customers',
    'Business partners',
    'Maintain customer organizations independently of their delivery sites.',
  ],
  'customer-sites': [
    'Customer sites',
    'Business partners',
    'Connect customer sites to their physical locations.',
  ],
  'fab-phases': [
    'Fab phases',
    'Business partners',
    'Register customer fab phases for operational references.',
  ],
  suppliers: [
    'Suppliers',
    'Business partners',
    'Maintain supplier locations, Incoterms and shared payment terms.',
  ],
  carriers: [
    'Carriers',
    'Business partners',
    'Maintain carrying parties, contacts and tracking-reference formats.',
  ],
  currencies: [
    'Currencies',
    'Finance & pricing',
    'Maintain currency codes, symbols and decimal precision.',
  ],
  'exchange-rates': [
    'Exchange rates',
    'Finance & pricing',
    'Maintain dated currency-pair rates and their sources.',
  ],
  'payment-terms': [
    'Payment terms',
    'Finance & pricing',
    'Define reusable due-date rules with preserved history.',
  ],
  'payment-calendars': [
    'Payment calendars',
    'Finance & pricing',
    'Maintain country-specific non-working dates and working-day patterns.',
  ],
  'tariff-rates': [
    'Tariff rates',
    'Finance & pricing',
    'Maintain duty rates by classification with dated source records.',
  ],
  'billing-settings': [
    'Billing settings',
    'Finance & pricing',
    'Maintain invoice patterns, billing rules and organization details.',
  ],
  'tax-exemptions': [
    'Tax exemptions',
    'Finance & pricing',
    'Maintain certificate and export exemptions by customer, product and delivery site.',
  ],
  'product-prices': [
    'Product prices',
    'Finance & pricing',
    'Submit buy and sell prices by product, customer and location for a second-person decision.',
  ],
  'batch-code-conversions': [
    'Batch code conversions',
    'Logistics',
    'Maintain the batch-code month-letter reference table.',
  ],
  'supply-routes': [
    'Supply routes',
    'Logistics',
    'Connect factories and locations with a configurable transport mode.',
  ],
  'route-legs': [
    'Route legs',
    'Logistics',
    'Maintain sequenced legs, durations, units and capacity limits per route.',
  ],
  'logger-recorder-configurations': [
    'Recorder configuration',
    'Logistics',
    'Maintain recorder formats, reading intervals and placement requirements.',
  ],
  'document-types': [
    'Document types',
    'Documents & compliance',
    'Maintain the shared vocabulary for document classes.',
  ],
  'asn-prerequisite-configurations': [
    'ASN prerequisites',
    'Documents & compliance',
    'Configure which certificate submissions are required before an ASN.',
  ],
  'document-settings': [
    'Document settings',
    'Documents & compliance',
    'Maintain numbering, filenames, field selection and standing text by document type and origin.',
  ],
  'retention-policies': [
    'Retention policies',
    'Documents & compliance',
    'Maintain retention periods and archival rules per document type.',
  ],
  'document-signing-authorities': [
    'Signing authorities',
    'Documents & compliance',
    'Authorize either a business role or an individual employee to sign a document type.',
  ],
  employees: [
    'Employees',
    'People & configuration',
    'Maintain employee records used for authorship, approval, signing and alert delivery.',
  ],
  'business-settings': [
    'Business settings',
    'People & configuration',
    'Maintain named configuration values by module with effective dates.',
  ],
  'calculation-rules': [
    'Calculation rules',
    'People & configuration',
    'Maintain formulas, rounding policies and parameter references.',
  ],
  'validation-rules': [
    'Validation rules',
    'People & configuration',
    'Maintain blocking checks, responsible roles and rejection messages.',
  ],
  'warning-rules': [
    'Warning rules',
    'People & configuration',
    'Maintain advisory warnings independently of blocking validation.',
  ],
  'alert-subscriptions': [
    'Alert subscriptions',
    'People & configuration',
    'Route alerts to employees through email or in-app delivery.',
  ],
};

export const entities: EntityDefinition[] = contracts.map((original) => {
  // Deployed PR #44 derives price authors from the authenticated account.
  // The generated schema predates the router's optional legacy identity field.
  let contract =
    original.id === 'storage-rooms'
      ? { ...original, sort: [] }
      : original.id === 'product-prices'
        ? { ...original, create: original.create.filter((field) => field.name !== 'entered_by') }
        : original;
  if (contract.id === 'alert-subscriptions') {
    const choices = [
      'inventory_shortage',
      'inventory_overstock',
      'inventory_forecast_divergence',
      'temperature_excursion',
      'missing_warehouse_report',
    ];
    contract = {
      ...contract,
      create: contract.create.map((field): FieldSpec =>
        field.name === 'alert_type' ? { ...field, kind: 'enum', options: choices } : field
      ),
    };
  }
  if (contract.id === 'product-lifecycle-parameters') {
    const unitField = (field: FieldSpec): FieldSpec => field.name === 'duration_uom'
      ? { ...field, kind: 'enum', required: true, options: [...lifecycleUnits] } : field;
    contract = { ...contract, create: contract.create.map(unitField), update: contract.update.map(unitField) };
  }
  const info = presentation[contract.id];
  if (!info) throw new Error(`Missing presentation for ${contract.id}`);
  return { ...contract, title: info[0], group: info[1], description: info[2] };
});
export const groups = [
  'Products',
  'Locations & facilities',
  'Business partners',
  'Finance & pricing',
  'Logistics',
  'Documents & compliance',
  'People & configuration',
];
export function entityById(id: string) {
  return entities.find((entity) => entity.id === id);
}

export const references: Record<string, { entity: string; value: string }> = {
  transport_mode: { entity: 'transport-modes', value: 'code' },
  origin: { entity: 'document-origin-countries', value: 'origin' },
  product_id: { entity: 'products', value: 'id' },
  customer_id: { entity: 'customers', value: 'id' },
  location_id: { entity: 'locations', value: 'id' },
  origin_location_id: { entity: 'locations', value: 'id' },
  destination_location_id: { entity: 'locations', value: 'id' },
  arrival_location_id: { entity: 'locations', value: 'id' },
  supplier_code: { entity: 'suppliers', value: 'supplier_code' },
  factory_id: { entity: 'factories', value: 'id' },
  warehouse_id: { entity: 'warehouses', value: 'id' },
  storage_room_id: { entity: 'storage-rooms', value: 'id' },
  route_id: { entity: 'supply-routes', value: 'id' },
  currency_id: { entity: 'currencies', value: 'id' },
  from_currency_code: { entity: 'currencies', value: 'currency_code' },
  to_currency_code: { entity: 'currencies', value: 'currency_code' },
  payment_terms_code: { entity: 'payment-terms', value: 'term_code' },
  document_type_id: { entity: 'document-types', value: 'id' },
  entered_by: { entity: 'employees', value: 'id' },
  approved_by: { entity: 'employees', value: 'id' },
  recipient_id: { entity: 'employees', value: 'id' },
  signer_employee_id: { entity: 'employees', value: 'id' },
  fab_phase_id: { entity: 'fab-phases', value: 'id' },
  authorized_by: { entity: 'employees', value: 'id' },
};

// Joined API rows contain several business codes. Labels must use the owning entity.
export const recordIdentity: Record<string, readonly [string, string]> = {
  'issuing-entities': ['entity_code', 'legal_name'],
  'charge-types': ['code', 'name'],
  products: ['product_code', 'product_name'],
  locations: ['location_code', 'location_name'],
  factories: ['factory_code', 'factory_name'],
  warehouses: ['warehouse_code', 'warehouse_name'],
  'storage-rooms': ['room_code', 'room_name'],
  'warehouse-storage-bins': ['bin_code', 'bin_name'],
  customers: ['customer_code', 'customer_name'],
  'customer-sites': ['site_code', 'site_name'],
  suppliers: ['supplier_code', 'supplier_name'],
  carriers: ['carrier_code', 'carrier_name'],
  currencies: ['currency_code', 'currency_name'],
  employees: ['employee_code', 'employee_name'],
  'document-types': ['document_type_code', 'document_name'],
  'payment-terms': ['term_code', 'description'],
  'supply-routes': ['route_code', 'route_name'],
  'business-settings': ['setting_key', 'description'],
  'calculation-rules': ['rule_code', 'rule_name'],
  'validation-rules': ['rule_code', 'rule_name'],
  'warning-rules': ['warning_code', 'warning_name'],
  'logger-recorder-configurations': ['recorder_model', 'description'],
  'fab-phases': ['phase_code', 'description'],
  uoms: ['code', 'display_name'],
  'batch-code-conversions': ['code_value', 'description'],
};

export const versionIdentityFields: Record<string, string> = {
  carriers: 'carrier_code',
  suppliers: 'supplier_code',
  'payment-terms': 'term_code',
};

export const createActionLabels: Record<string, string> = {
  carriers: 'Add carrier',
  suppliers: 'Add supplier',
  'payment-terms': 'Add payment term',
};

const labels: Record<string, string> = {
  supplier_code: 'Supplier',
  factory_id: 'Factory',
  exemption_basis: 'Exemption basis',
  source: 'Source',
  site: 'Site',
  q: 'Search code or name',
  active: 'Status',
  is_active: 'Active',
  price_type: 'Price side',
  entered_by: 'Entered by',
  approved_by: 'Reviewer',
  effective_from: 'Effective from',
  effective_to: 'Effective until',
  record_type: 'Record type',
  revision_flag: 'Revision',
  storage_room_id: 'Storage room',
  recipient_id: 'Recipient',
  from_currency_code: 'From currency',
  to_currency_code: 'To currency',
  payment_terms_code: 'Payment terms',
  date: 'As of date',
  duration_uom: 'Duration unit',
  reading_uom: 'Reading unit',
  forecast_divergence_threshold_percent: 'Forecast divergence threshold (%)',
  inventory_floor_days: 'Inventory floor (days)',
  maximum_inventory_days: 'Inventory ceiling (days)',
};
export function fieldLabel(name: string): string {
  const label = labels[name] ?? name.replace(/_id$/, '').replaceAll('_', ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}
export const guidance: Record<string, string> = {
  'calculation-rules':
    'These records document calculation definitions. Saving a formula does not make it executable; confirm its operational behavior before relying on it.',
  'validation-rules':
    'These records describe agreed checks. Saving a condition here does not automatically enforce it in every workflow.',
  'warning-rules':
    'These records describe advisory warnings. Confirm the corresponding workflow implements the warning before relying on it.',
  'warehouse-storage-bins':
    'Bins identify storage positions within a warehouse. Confirm the meaning of optional zone/type labels with your operations team.',
  'product-information-records':
    'Source snapshots are reference-only. Revise a working record with an explicit revision flag and reason. These records hold metadata; the source spreadsheet is not uploaded here.',
  'product-prices':
    'A submitted price stays pending until a different employee approves it. The price unit or basis remains unconfirmed. Buy and sell timelines are independent. Backdated prices are allowed; review the effective date carefully. A BUY price can be specific to a supplier, and to one of that supplier’s factories. The most specific approved price applies; the general price (no supplier) applies to every other source.',
  'tax-exemptions':
    'A certificate exemption needs its certificate reference; an export exemption does not. An exemption can apply to one delivery site, which then takes precedence over the customer-wide record there. Without an effective exemption, billing treats the line as taxed.',
  'inventory-planning-settings':
    'Inventory floor and ceiling are advisory. A new effective date preserves the previous settings.',
  'document-signing-authorities':
    'Choose an active application role (its exact name, such as LOGISTICS) OR a signer employee, and identify the employee authorizing the grant. Inactive or unrecognized roles cannot authorize signing.',
  'fab-phases': 'Fab phases can be registered and viewed. Existing phase records cannot be edited.',
  uoms: 'Units are shared by every product, conversion, price and report. A unit’s code cannot be changed after creation; create a new unit instead. L, BTL, BOX and PLT are system units and cannot be deleted, and a unit still in use cannot be deleted. Maintain product-specific factors in Packaging & units on the product.',
  'alert-subscriptions':
    'Create separate subscriptions for EMAIL and IN_APP when the recipient needs both channels.',
  'payment-calendars':
    'Add one record per non-working date. The calendar year is derived from that date.',
  'storage-room-assignments':
    'A product without a storage assignment has no room against which its historical temperature readings can be judged.',
};
